import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Markets from './pages/Markets';
import Services from './pages/Services';
import Education from './pages/Education';
import Platforms from './pages/Platforms';
import MT5 from './pages/MT5';
import MT4 from './pages/MT4';
import CTraders from './pages/CTraders';
import IBPartners from './pages/IBPartners';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import TradingSignals from './pages/TradingSignals';
import Webinar from './pages/services/Webinar';
import ServiceEducation from './pages/services/Education';  // Renamed to avoid conflict with main Education
import Forex from './pages/markets/Forex';
import Indices from './pages/markets/Indices';
import Commodity from './pages/markets/Commodity';
import Crypto from './pages/markets/Crypto';
import Stocks from './pages/markets/Stocks';
import Beginner from './pages/education/Beginner';
import Intermediate from './pages/education/Intermediate';
import Advanced from './pages/education/Advanced';
import ForexEducation from './pages/education/ForexEducation';
import CommodityEducation from './pages/education/CommodityEducation';
import IndicesEducation from './pages/education/IndicesEducation';
import StocksEducation from './pages/education/StocksEducation';
import CryptoEducation from './pages/education/CryptoEducation';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTest from './components/AdminTest';
import { useScrollEffect, useIntersectionObserver, useInitAnimations } from './utils/scrollEffects';
import './styles/App.css';

function App() {
  useScrollEffect();
  useIntersectionObserver();
  useInitAnimations();
  
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <Router>
          <div className="App">
            {/* TradingView Ticker Tape - Global top widget */}
            <div className="tradingview-widget-container">
              <div className="tradingview-widget">
                <iframe 
                  src="https://s.tradingview.com/embed-widget/ticker-tape/?locale=en#%7B%22symbols%22%3A%5B%7B%22proName%22%3A%22FX_IDC%3AEURUSD%22%2C%22title%22%3A%22EUR%2FUSD%22%7D%2C%7B%22proName%22%3A%22FX_IDC%3AGBPUSD%22%2C%22title%22%3A%22GBP%2FUSD%22%7D%2C%7B%22proName%22%3A%22FX_IDC%3AUSDJPY%22%2C%22title%22%3A%22USD%2FJPY%22%7D%2C%7B%22proName%22%3A%22BITSTAMP%3ABTCUSD%22%2C%22title%22%3A%22BTC%2FUSD%22%7D%2C%7B%22proName%22%3A%22BITSTAMP%3AETHUSD%22%2C%22title%22%3A%22ETH%2FUSD%22%7D%2C%7B%22proName%22%3A%22OANDA%3AXAUUSD%22%2C%22title%22%3A%22GOLD%22%7D%2C%7B%22proName%22%3A%22OANDA%3AXAGUSD%22%2C%22title%22%3A%22SILVER%22%7D%2C%7B%22proName%22%3A%22FOREXCOM%3ASPXUSD%22%2C%22title%22%3A%22S%26P%20500%22%7D%2C%7B%22proName%22%3A%22FOREXCOM%3ANSXUSD%22%2C%22title%22%3A%22US%20100%22%7D%2C%7B%22proName%22%3A%22TVC%3AUSOIL%22%2C%22title%22%3A%22OIL%22%7D%5D%2C%22showSymbolLogo%22%3Atrue%2C%22colorTheme%22%3A%22dark%22%2C%22isTransparent%22%3Atrue%2C%22displayMode%22%3A%22adaptive%22%2C%22locale%22%3A%22en%22%7D"
                  width="100%" 
                  height="36" 
                  frameBorder={0} 
                  title="Market Ticker"
                />
              </div>
            </div>
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin-test" element={<AdminTest />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/secure/admin/login" element={<AdminLogin />} />
              <Route path="/secure/admin/dashboard" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              
              {/* Public Routes with Header/Footer */}
              <Route path="/*" element={
                <>
                  <Header />
                  <main>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/markets" element={<Markets />} />
                      <Route path="/markets/forex" element={<Forex />} />
                      <Route path="/markets/indices" element={<Indices />} />
                      <Route path="/markets/commodity" element={<Commodity />} />
                      <Route path="/markets/crypto" element={<Crypto />} />
                      <Route path="/markets/stocks" element={<Stocks />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/education" element={<Education />} />
                      <Route path="/education/beginner" element={<Beginner />} />
                      <Route path="/education/intermediate" element={<Intermediate />} />
                      <Route path="/education/advanced" element={<Advanced />} />
                      <Route path="/education/forex" element={<ForexEducation />} />
                      <Route path="/education/commodity" element={<CommodityEducation />} />
                      <Route path="/education/indices" element={<IndicesEducation />} />
                      <Route path="/education/stocks" element={<StocksEducation />} />
                      <Route path="/education/crypto" element={<CryptoEducation />} />
                      <Route path="/platforms" element={<Platforms />} />
                      <Route path="/platforms/mt5" element={<MT5 />} />
                      <Route path="/platforms/mt4" element={<MT4 />} />
                      <Route path="/platforms/ctraders" element={<CTraders />} />
                      <Route path="/ib-partners" element={<IBPartners />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/contact" element={<ContactUs />} />
                      <Route path="/services/signals" element={<TradingSignals />} />
                      <Route path="/services/webinar" element={<Webinar />} />
                      <Route path="/services/education" element={<ServiceEducation />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
          </div>
        </Router>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;